import { AccommodationCountService, BaseHttpRequest, CancelablePromise } from '@nocowanie/noc-api';

import { environment } from '@app/environments/environment';
import { ListingFilterCountArgs } from '@app/helpers/listing-counters.helpers';

import type { ApiRequestOptions } from 'libs/nocapi/src/lib/core/ApiRequestOptions';
import { getQueryString } from 'libs/nocapi/src/lib/core/request';

/**
 * Loads listing filter counters without competing with page load:
 * - requests from all filter groups are collected and sent as one batch,
 * - the first batch waits for `window.onload` and then for browser idle time,
 * - requests run in a Web Worker (main-thread fallback), and all results of a batch are
 *   delivered in a single task, so React renders the counters once instead of per request.
 *
 * The worker is created from a Blob: chunks are served from the CDN (`assetPrefix`) and
 * browsers don't allow cross-origin worker scripts. It only fetches prebuilt URLs.
 */

type CountsCallback = (counts: (number | undefined)[]) => void;

interface CounterRequest {
    urls: string[];
    onDone: CountsCallback;
    cancelled: boolean;
}

interface WorkerRequest {
    batchId: number;
    urls: string[];
}

interface WorkerResponse {
    batchId: number;
    /** `null` when the request failed */
    counts: Record<string, number | null>;
}

const IDLE_TIMEOUT_MS = 3000;
// Browser has ~6 connections per host; keep a couple free for the rest of the page
const MAX_CONCURRENT_REQUESTS = 4;

const workerSource = `
self.onmessage = async function (event) {
    var batchId = event.data.batchId;
    var urls = event.data.urls;
    var counts = {};
    var next = 0;

    async function runNext() {
        var url = urls[next++];
        if (url === undefined) return;
        try {
            var response = await fetch(url, { headers: { Accept: 'application/json' } });
            var body = response.ok ? await response.json() : null;
            counts[url] = body && typeof body.count === 'number' ? body.count : null;
        } catch (error) {
            counts[url] = null;
        }
        return runNext();
    }

    var runners = [];
    for (var i = 0; i < Math.min(${MAX_CONCURRENT_REQUESTS}, urls.length); i++) runners.push(runNext());
    await Promise.all(runners);

    self.postMessage({ batchId: batchId, counts: counts });
};
`;

/** Captures request options instead of sending them, to reuse the generated client's URL logic */
class CapturingHttpRequest extends BaseHttpRequest {
    public captured?: ApiRequestOptions;

    public override request<T>(options: ApiRequestOptions): CancelablePromise<T> {
        this.captured = options;
        return undefined as unknown as CancelablePromise<T>;
    }
}

const capturingRequest = new CapturingHttpRequest({
    BASE: '',
    VERSION: '0.1.0',
    WITH_CREDENTIALS: false,
    CREDENTIALS: 'include',
});
const capturingCountService = new AccommodationCountService(capturingRequest);

/** Same URL `sharedNocApiClient.accommodationCount.countAccommodationForListing` requests */
export const getListingFilterCountUrl = (args: ListingFilterCountArgs): string => {
    capturingCountService.countAccommodationForListing(...args);
    const { url, query } = capturingRequest.captured as ApiRequestOptions;
    // Absolute, so it resolves the same inside the (blob) worker. `new URL` appends `/` to an
    // origin-only base; the generated client concatenates `BASE + url` as is, so drop that slash.
    const configuredBase = environment.NOC_API_END_POINT ?? '';
    const absoluteBase = new URL(configuredBase, window.location.href).href;
    const base = configuredBase.endsWith('/') ? absoluteBase : absoluteBase.replace(/\/$/, '');

    return `${base}${url}${query ? getQueryString(query) : ''}`;
};

let worker: Worker | null | undefined;
let batchSeq = 0;
let isFlushScheduled = false;
let queuedRequests: CounterRequest[] = [];
const inflightBatches = new Map<number, { requests: CounterRequest[]; urls: string[] }>();

function handleResponse({ batchId, counts }: WorkerResponse) {
    const batch = inflightBatches.get(batchId);
    inflightBatches.delete(batchId);

    batch?.requests.forEach(request => {
        if (!request.cancelled) {
            request.onDone(request.urls.map(url => counts[url] ?? undefined));
        }
    });
}

const fetchOnMainThread = async ({ batchId, urls }: WorkerRequest) => {
    const counts: WorkerResponse['counts'] = {};

    await Promise.all(
        urls.map(async url => {
            try {
                const response = await fetch(url, { headers: { Accept: 'application/json' } });
                const body = response.ok ? await response.json() : null;
                counts[url] = typeof body?.count === 'number' ? body.count : null;
            } catch {
                counts[url] = null;
            }
        }),
    );

    handleResponse({ batchId, counts });
};

const getWorker = (): Worker | null => {
    if (worker !== undefined) {
        return worker;
    }

    try {
        const blobUrl = URL.createObjectURL(
            new Blob([workerSource], { type: 'application/javascript' }),
        );
        worker = new Worker(blobUrl);
        URL.revokeObjectURL(blobUrl);

        worker.onmessage = (event: MessageEvent<WorkerResponse>) => handleResponse(event.data);
        worker.onerror = () => {
            // Worker unusable (e.g. blocked by CSP): finish pending batches on the main thread
            worker?.terminate();
            worker = null;
            inflightBatches.forEach(
                ({ urls }, batchId) => void fetchOnMainThread({ batchId, urls }),
            );
        };
    } catch {
        worker = null;
    }

    return worker;
};

const flush = () => {
    isFlushScheduled = false;

    const requests = queuedRequests.filter(request => !request.cancelled);
    queuedRequests = [];

    const urls = [...new Set(requests.flatMap(request => request.urls))];

    if (!urls.length) {
        return;
    }

    const message: WorkerRequest = { batchId: ++batchSeq, urls };
    inflightBatches.set(message.batchId, { requests, urls });

    const counterWorker = getWorker();

    if (counterWorker) {
        counterWorker.postMessage(message);
    } else {
        void fetchOnMainThread(message);
    }
};

const runWhenPageIsIdle = (callback: () => void) => {
    const scheduleIdle = () => {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
        } else {
            setTimeout(callback, 200);
        }
    };

    if (document.readyState === 'complete') {
        scheduleIdle();
    } else {
        window.addEventListener('load', scheduleIdle, { once: true });
    }
};

/**
 * Queues filter counter requests. `onDone` receives counts in `argsList` order
 * (`undefined` for failed requests). Returns a cancel function.
 */
export const requestFilterCounts = (
    argsList: ListingFilterCountArgs[],
    onDone: CountsCallback,
): (() => void) => {
    const request: CounterRequest = {
        urls: argsList.map(getListingFilterCountUrl),
        onDone,
        cancelled: false,
    };

    queuedRequests.push(request);

    if (!isFlushScheduled) {
        isFlushScheduled = true;
        runWhenPageIsIdle(flush);
    }

    return () => {
        request.cancelled = true;
    };
};
